import { hasPermission } from "@avela/avela-authorization-sdk";
import { Button, Divider, Flex, Heading, Spacer } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React, { ChangeEvent, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { NavLink } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { NotFound } from "src/components/Feedback/NotFound";
import { GET_PERSON_RELATIONSHIPS } from "src/components/graphql/queries";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { DetailHeader } from "src/components/Layout/DetailHeader";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import {
  PERMISSIONS_AUTHORIZATION_SCOPE,
  useAuthorizationScoped,
} from "src/components/Providers/AuthorizationProvider";
import { RelationshipsTable } from "src/components/Table/RelationshipsTable";
import { RelationshipTypes } from "src/constants";
import { useAuth0ClientId } from "src/hooks/useAuth0ClientId";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { ContactInfoSchema } from "src/schemas/Parent";
import * as breadcrumb from "src/services/breadcrumb";
import { validateWithZod } from "src/services/formValidations";
import { isLinkedLogin } from "src/services/person";
import { isNotNull } from "src/services/predicates";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { ContactForm } from "../components/Forms/ContactForm";
import { ParentForm } from "../components/Forms/ParentForm";
import { EditUserProfile } from "../userProfile";
import { AddStudentsButton } from "./components/AddStudentsButton";
import { UpdateContactInfoError } from "./graphql/errors";
import { UPDATE_CONTACT_INFO, UPDATE_PARENT } from "./graphql/mutations";
import { GET_PARENTS, GET_PARENT_BY_ID } from "./graphql/queries";
import {
  EmailRequiredParentProfileValidator,
  ParentProfileFormType,
  ParentProfileSchema,
  ParentProfileValidator,
  PhoneRequiredParentProfileValidator,
} from "./schemas";

// TODO: separate component for View so that we don't have this editingDisabled prop
export const EditParent = ({ editingDisabled = false }) => {
  const [submitting, setSubmitting] = React.useState(false);
  const { id = "" } = useParams();
  const organization = useOrganization();
  const toast = useAvelaToast();
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState<boolean>();
  const clientIdRD = useAuth0ClientId();

  const { remoteData } = useRemoteDataQuery<
    GQL.GetParent,
    GQL.GetParentVariables
  >(GET_PARENT_BY_ID, {
    variables: { id },
  });

  const [updateParent, { remoteData: updateRemoteData }] =
    useRemoteDataMutation<GQL.UpdateParent, GQL.UpdateParentVariables>(
      UPDATE_PARENT
    );

  const [updateContactInfo, { remoteData: contactInfoRemoteData }] =
    useRemoteDataMutation<
      GQL.UpdateContactInfo,
      GQL.UpdateContactInfoVariables
    >(UPDATE_CONTACT_INFO);

  const { remoteData: studentRelationships } = useRemoteDataQuery<
    GQL.GetPersonRelationships,
    GQL.GetPersonRelationshipsVariables
  >(GET_PERSON_RELATIONSHIPS, {
    variables: {
      id,
    },
  });

  const authorizationMap = useAuthorizationScoped(
    PERMISSIONS_AUTHORIZATION_SCOPE
  );
  // TODO: this will become a more specific permission check
  const isAllowedToEditContactInfo = React.useMemo(
    () =>
      hasPermission({
        action: {
          type: "Perform::Action",
          id: "with_permission",
        },
        resource: {
          type: "Permission",
          id: "user:update",
        },
        authorizationMap: authorizationMap.hasData()
          ? authorizationMap.data
          : {},
      }),
    [authorizationMap]
  );

  const handleSubmit = async (values: GQL.person_set_input) => {
    remoteData.do(async ({ person_by_pk: currentPerson }) => {
      organization.do(async (org) => {
        if (isActive !== undefined) values.active = isActive;

        setSubmitting(true);
        try {
          const contactInfo = ContactInfoSchema.parse(values);

          let parentValidator = ParentProfileValidator;

          // if we have a user ID, update contact info with the update-contact-info action
          // otherwise, insert the contact info with the parent
          // this is done to ensure contact info is still updated when a user_id is not available
          if (currentPerson?.user?.id && clientIdRD.hasData()) {
            // Only update contact info if it has actually changed
            // The form and auth0 data may not agree about null and empty strings, so normalize them to empty strings
            const previousPhoneNumber = currentPerson.phone_number ?? "";
            const previousEmailAddress = currentPerson.email_address ?? "";
            const newPhoneNumber = contactInfo.phone_number ?? "";
            const newEmailAddress = contactInfo.email_address ?? "";
            const contactInfoChanged =
              previousPhoneNumber !== newPhoneNumber ||
              previousEmailAddress !== newEmailAddress;

            if (contactInfoChanged) {
              // this call updates both the auth0 user profile and avela user profile
              const result = await updateContactInfo({
                variables: {
                  person_id: currentPerson.id,
                  client_id: clientIdRD.data.clientId,
                  phone_number: newPhoneNumber,
                  email_address: newEmailAddress,
                },
              });
              if (result.errors?.length) {
                console.error(
                  result.data?.update_contact_info?.status_code,
                  JSON.stringify(result.errors)
                );
                throw new Error(result.errors[0]?.message);
              }
            }
          } else {
            parentValidator = parentValidator.merge(ContactInfoSchema);
          }

          const parent = parentValidator.parse(values);

          await updateParent({
            variables: { id, parent },
            refetchQueries: [
              GET_PARENTS,
              GET_PARENT_BY_ID,
              GET_PERSON_RELATIONSHIPS,
            ],
            awaitRefetchQueries: true,
          });

          toast({
            id: "update-parent",
            title: "Parent updated",
            isClosable: true,
            status: "info",
          });
          navigate(Url.OrgAdmin.Parents.index(org));
        } catch (err) {
          console.error(JSON.stringify(err));
          const errorMessage =
            err instanceof Error ? err.message : "Unknown error";
          toast.error(getUpdateContactInfoErrorToast(errorMessage));
        } finally {
          setSubmitting(false);
        }
      });
    });
  };

  const handleChangeStatus = async (event: ChangeEvent<HTMLInputElement>) => {
    const newActiveState = event.target.checked;
    setIsActive(newActiveState);

    try {
      await updateParent({
        variables: {
          id: id,
          parent: {
            active: newActiveState,
          },
        },
        refetchQueries: [GET_PARENTS, GET_PARENT_BY_ID],
      });

      toast({
        id: "update-parent-status",
        title: "Parent status updated",
        isClosable: true,
        status: "info",
      });
    } catch (err) {
      console.error(`${JSON.stringify(err)}`);
      setIsActive(!newActiveState); // Revert state on error
      toast.error({
        title: "Error updating parent status",
      });
    }
  };

  return (
    <GQLRemoteDataView
      remoteData={RD.toTuple(remoteData, studentRelationships)}
    >
      {([data, relationshipData]) => {
        if (data.person_by_pk === null) return <NotFound />;
        const parent: GQL.GetParent_person_by_pk = data.person_by_pk;
        const parsed = ParentProfileSchema.safeParse(parent);
        if (!parsed.success) {
          console.error(parsed.error);
          return <GenericError />;
        }

        const emailLinked = isLinkedLogin(
          parent.email_address,
          parent.user?.name
        );
        const phoneLinked = isLinkedLogin(
          parent.phone_number,
          parent.user?.name
        );

        const validate = (values: ParentProfileFormType) => {
          if (emailLinked) {
            return validateWithZod(EmailRequiredParentProfileValidator)(values);
          }
          if (phoneLinked) {
            return validateWithZod(PhoneRequiredParentProfileValidator)(values);
          }

          return validateWithZod(ParentProfileValidator)(values);
        };
        return (
          <Flex direction="column" minHeight="100%">
            {!editingDisabled && (
              <Breadcrumb
                items={breadcrumb.parent.getBreadcrumbsForEdit(
                  organization,
                  data.person_by_pk
                )}
                mb={8}
              />
            )}
            <Formik<ParentProfileFormType>
              initialValues={parsed.data}
              onSubmit={handleSubmit}
              validate={validate}
            >
              {({ values }) => {
                const linkedStudentIds = relationshipData.relationship_person
                  .map((item) =>
                    item.first?.id === parent.id
                      ? item.second?.id
                      : item.first?.id
                  )
                  .filter(isNotNull);
                return (
                  <Flex as={Form} direction="column" gap={12} flexGrow="1">
                    <DetailHeader
                      data={parent}
                      isActive={isActive}
                      type={GQL.person_type_enum.guardian}
                      onActiveChange={handleChangeStatus}
                      isLoading={
                        updateRemoteData.isLoading() ||
                        contactInfoRemoteData.isLoading()
                      }
                      isDisabled={editingDisabled}
                    />
                    <Flex direction="column" gap={6}>
                      <Heading as="h2" variant="admin">
                        Basic Information
                      </Heading>
                      <ParentForm isDisabled={editingDisabled} />
                    </Flex>
                    <Divider />
                    <RelationshipsTable
                      tableData={relationshipData.relationship_person
                        .map((item) =>
                          item.first?.id === parent.id
                            ? { id: item.id, person: item.second }
                            : { id: item.id, person: item.first }
                        )
                        .filter((item) => isNotNull(item.person))}
                      personId={data.person_by_pk?.id}
                      relationshipType={RelationshipTypes.Student}
                      readOnly={editingDisabled}
                      action={
                        <AddStudentsButton
                          organizationId={organization
                            .map((org) => org.id)
                            .withDefault("")}
                          parentId={parent.id}
                          parentName={[values.first_name, values.last_name]
                            .filter(Boolean)
                            .join(" ")}
                          parentAddress={{
                            street_address: values.street_address || null,
                            street_address_line_2:
                              values.street_address_line_2 || null,
                            city: values.city || null,
                            state: values.state || null,
                            zip_code: values.zip_code || null,
                          }}
                          excludeIds={[parent.id, ...linkedStudentIds]}
                        />
                      }
                    />
                    <Flex direction="column" gap={6}>
                      <Heading as="h2" variant="admin">
                        Contact Preferences
                      </Heading>
                      <ContactForm
                        emailLinked={emailLinked}
                        phoneLinked={phoneLinked}
                        isDisabled={
                          !isAllowedToEditContactInfo || editingDisabled
                        }
                      />
                    </Flex>
                    <EditUserProfile
                      personId={parent.id}
                      userId={parent.user?.id}
                      refetchQueriesAfterDelink={[GET_PARENT_BY_ID]}
                    />
                    <Spacer />
                    {!editingDisabled && (
                      <AdminFormButtons>
                        <Button
                          as={NavLink}
                          to={organization
                            .map((org) => Url.OrgAdmin.Parents.index(org))
                            .withDefault("#")}
                          isLoading={organization.isLoading()}
                          variant="outline"
                          colorScheme="gray"
                        >
                          Cancel
                        </Button>
                        <Spacer />
                        <Button
                          type="submit"
                          marginLeft={4}
                          isLoading={submitting}
                          isDisabled={editingDisabled}
                        >
                          Update
                        </Button>
                      </AdminFormButtons>
                    )}
                    {editingDisabled && (
                      <WithUserPermissions permissions={["user:update"]}>
                        <AdminFormButtons>
                          <Button
                            as={NavLink}
                            to={Url.OrgAdmin.Parents.edit(organization, id)}
                          >
                            Edit
                          </Button>
                        </AdminFormButtons>
                      </WithUserPermissions>
                    )}
                  </Flex>
                );
              }}
            </Formik>
          </Flex>
        );
      }}
    </GQLRemoteDataView>
  );
};

export const getUpdateContactInfoErrorToast = (
  errorCode: string
): { title: string; description?: string } => {
  switch (errorCode) {
    // Authorization errors
    case UpdateContactInfoError.NonGuardianUser:
      return {
        title: "Invalid user type",
        description:
          "Contact information can only be updated for guardian accounts. Contact support for help.",
      };
    case UpdateContactInfoError.CannotUpdateUserProfile:
      return {
        title: "Cannot update profile",
        description:
          "You don't have permission to update this user's profile. Contact the organization's administrator for help.",
      };

    // Validation errors
    case UpdateContactInfoError.EmailOrPhoneRequired:
      return {
        title: "Contact information required",
        description: "Either an email address or a phone number is required.",
      };
    case UpdateContactInfoError.NoIdentityProvider:
      return {
        title: "Identity provider not found",
        description: "Cannot find identity provider. Contact support for help.",
      };
    case UpdateContactInfoError.EmailRequired:
      return {
        title: "Email address required",
        description: "A valid email address is required.",
      };
    case UpdateContactInfoError.PhoneRequired:
      return {
        title: "Phone number required",
        description: "A valid phone number is required.",
      };

    // Auth0 errors
    case UpdateContactInfoError.EmailAlreadyExists:
      return {
        title: "Email already in use",
        description:
          "Email address in use by another account. Use a different email address or contact support if you believe this is an error.",
      };
    case UpdateContactInfoError.PhoneAlreadyExists:
      return {
        title: "Phone number already in use",
        description:
          "Phone number in use by another account. Use a different phone number or contact support if you believe this is an error.",
      };
    case UpdateContactInfoError.AccountAlreadyExists:
      return {
        title: "Account already exists",
        description:
          "Contact information in use by another account. Use a different email address or phone number, or contact support if you believe this is an error.",
      };
    case UpdateContactInfoError.InvalidEmailFormat:
      return {
        title: "Invalid email format",
        description:
          "Enter a valid email address in the format example@domain.com",
      };
    case UpdateContactInfoError.InvalidPhoneFormat:
      return {
        title: "Invalid phone number format",
        description: "Enter a valid phone number in the format +14155552671",
      };
    case UpdateContactInfoError.InvalidInformationFormat:
      return {
        title: "Invalid information format",
        description:
          "Check that all contact information is entered in the correct format.",
      };
    case UpdateContactInfoError.Auth0AuthenticationFailed:
      return {
        title: "Authentication failed",
        description:
          "Cannot authenticate with our identity provider. Try again later, or contact support for help.",
      };
    case UpdateContactInfoError.Auth0AccessDenied:
      return {
        title: "Access denied",
        description:
          "You don't have permission to update this contact information. Contact the organization's administrator for help.",
      };
    case UpdateContactInfoError.Auth0UserNotFound:
      return {
        title: "User not found",
        description:
          "The user account could not be found. Contact support for help.",
      };
    case UpdateContactInfoError.Auth0RateLimit:
      return {
        title: "Too many requests",
        description:
          "Too many update attempts. Wait a few minutes before trying again.",
      };
    case UpdateContactInfoError.Auth0ServiceUnavailable:
      return {
        title: "Service temporarily unavailable",
        description:
          "Our identity service is temporarily unavailable. Try again in a few minutes.",
      };
    case UpdateContactInfoError.Auth0OperationFailed:
      return {
        title: "Something went wrong",
        description: "Update failed. Try again or contact support for help.",
      };

    // Database errors
    case UpdateContactInfoError.DatabaseEmailUpdate:
      return {
        title: "Email update failed",
        description:
          "Unable to update the email address in our database. Try again or contact support for help.",
      };
    case UpdateContactInfoError.DatabasePhoneUpdate:
      return {
        title: "Phone number update failed",
        description:
          "Unable to update the phone number in our database. Try again or contact support for help.",
      };

    case UpdateContactInfoError.MultiOrgUserNotSupported:
      return {
        title: "Cannot update shared profile",
        description:
          "This parent/guardian is associated with multiple organizations. Contact information updates are not supported for shared profiles.",
      };

    default:
      return {
        title: "Error updating parent",
      };
  }
};
